import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { UserSession } from './models/user-session';
import { ServicerProvider } from './servicer';
import { CancelRequestParameter } from './models/user-cancel-request-parameter';
import { CancelRequestResponse } from './models/user-cancel-request-response';
import { RetrieveRequestResponse } from './models/user-retrieve-request-response';
import { CreateRequestParameter } from './models/user-create-request-parameter';
import { CreateRequestResponse } from './models/user-create-request-response';
import { ListAllRequestsResponse } from './models/user-list-all-requests-response';
import { ListAllRequestsParameter } from './models/user-list-all-requests-parameter';
import { RetrieveVehicleResponse } from './models/user-retrieve-vehicle-response';
import { SpareGetServiceIdParameter } from './models/spare-get-service-id-parameter';
import { SpareGetServiceIdResponse } from './models/spare-get-service-id-response';
import { SpareGetNextAvailableRideParameter } from './models/spare-get-next-available-ride-parameter';
import { SpareGetNextAvailableRideResponse } from './models/spare-get-next-available-ride-response';
import { ListAllServicesParameter } from './models/user-list-all-services-parameter';
import { ListAllServicesResponse } from './models/user-list-all-services-response';
import { SpareGetRequestRideParameter } from './models/spare-get-request-ride-parameter';
import { SpareGetRequestRideResponse } from './models/spare-get-request-ride-response';
import { environment } from '../../environments/environment';

/**
 * ユーザV2サービスプロバイダー.
 */
@Injectable({
  providedIn: 'root'
})
export class UserV2ServiceProvider extends ServicerProvider {
  private sessionSource: BehaviorSubject<UserSession> = new BehaviorSubject<UserSession>(null);

  constructor(
    public http: HttpClient
  ) {
    super(http);
  }

  /**
   * セッションを取得する.
   */
  getSession(): UserSession {
    return this.sessionSource.getValue();
  }

  /**
   * セッションを削除する.
   */
  deleteSession() {
    this.sessionSource.next(null);
  }

  /**
   * セッションを保存する.
   */
  saveSession(session: UserSession) {
    this.sessionSource.next(session);
  }

  /**
   * サービス検索Spare社API
   * @param parameter 
   * @returns 
   */
  public spareGetServiceId(parameter: SpareGetServiceIdParameter): Observable<SpareGetServiceIdResponse> {
    return this.get<SpareGetServiceIdParameter, SpareGetServiceIdResponse>(
      '/v1/estimates/services',
      parameter);
  }

  /**
   * 予約見積Spare社API(オンデマンド)
   * @param parameter 
   * @returns 
   */
  public spareGetNextAvailableRide(parameter: SpareGetNextAvailableRideParameter): Observable<SpareGetNextAvailableRideResponse> {
    return this.get<SpareGetNextAvailableRideParameter, SpareGetNextAvailableRideResponse>(
      '/v1/estimates/nextAvailable',
      parameter);
  }

  /**
   * 予約見積Spare社API(事前予約)
   * @param parameter 
   * @returns 
   */
  public spareGetRequestRide(parameter: SpareGetRequestRideParameter): Observable<SpareGetRequestRideResponse> {
    return this.get<SpareGetRequestRideParameter, SpareGetRequestRideResponse>(
      '/v1/estimates/request',
      parameter);
  }

  /**
   * 配車キャンセル.
   * 
   * @param parameter CancelRequestParameter
   * @returns Observable<UserDispatchCancelResponse>
   */
  cancelRequest(parameter: CancelRequestParameter): Observable<CancelRequestResponse> {
    return this.post<CancelRequestParameter, CancelRequestResponse>(
      '/v1/requests/' + parameter.id + '/cancellation',
      parameter);
  }

  /**
   * 配車予約.
   * 配車予約を行う。
   *
   * @param parameter CreateRequestParameter
   * @returns Observable<CreateRequestResponse>
   */
  createRequest(parameter: CreateRequestParameter): Observable<CreateRequestResponse>{
    return this.post<CreateRequestParameter, CreateRequestResponse>(
      '/v1/requests',
      parameter);
  }

  /**
   * 予約情報取得.
   * 予約IDで予約ステータスを確認する。
   *
   * @param parameter string
   * @returns Observable<RetrieveRequestResponse>
   */
  retrieveRequest(parameter: string): Observable<RetrieveRequestResponse> {
    return this.get<string, RetrieveRequestResponse>(
      '/v1/requests/' + parameter,
      null);
  }

  /**
   * 車両情報取得.
   * 車両IDで車両情報を取得する。
   *
   * @param parameter string
   * @returns Observable<RetrieveVehicleResponse>
   */
  retrieveVehicle(parameter: string): Observable<RetrieveVehicleResponse> {
    return this.get<string, RetrieveVehicleResponse>(
      '/v1/vehicles/' + parameter,
      null);
  }

  /**
   * 予約一覧取得.
   * 予約一覧を取得する。
   *
   * @param parameter ListAllRequestsParameter
   * @returns Observable<ListAllRequestsResponse>
   */
  listAllRequests(parameter: ListAllRequestsParameter): Observable<ListAllRequestsResponse> {
    return this.get<ListAllRequestsParameter, ListAllRequestsResponse>(
      '/v1/requests',
      parameter);
  }

  /**
   * サービス一覧取得.
   * サービス一覧を取得する。
   *
   * @param parameter ListAllServicesParameter
   * @returns Observable<ListAllServicesResponse>
   */
  listAllServices(parameter: ListAllServicesParameter): Observable<ListAllServicesResponse> {
    return this.get<ListAllServicesParameter, ListAllServicesResponse>(
      '/v1/services',
      parameter);
  }

  /**
   * 営業時間取得.
   * @param なし
   * @return true: 成功 false: 失敗
   */
  public getOpeningHours() : Observable<Boolean>{
    const spareServiceIds = ((obj, key) => obj[key])(environment.spareServices, environment.profile);
    return new Observable<Boolean>((observe) => {
      this.listAllServices({
        ids: spareServiceIds
      }).subscribe((response) => {
        if (Object.keys(response?.data).length > 0 &&
            response.data.some((service) => spareServiceIds.includes(service.id))) {
          const services= response.data.filter((service) => spareServiceIds.includes(service.id));
          const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const today = new Date();
          const todayName = DAY_NAMES[today.getDay()];
          const isWeekDay = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(todayName);
          const isWeekEnd = ['sunday', 'saturday'].includes(todayName);
          const serviceTs: number[] = [];

          services.map((service) => {
            const todayOpeningHours = service.timeRules.find(timeRule => {
              const serviceDay = timeRule.days;
              return (
                serviceDay === todayName ||
                (serviceDay === 'weekdays' && isWeekDay) ||
                (serviceDay === 'weekends' && isWeekEnd) ||
                serviceDay === 'everyday'
              );
            });
            if (todayOpeningHours) {
              serviceTs.push(todayOpeningHours.startTs);
              serviceTs.push(todayOpeningHours.endTs);
            }
          });

          if (serviceTs.length === 0) {
            // 営業時間なし
            observe.next(false);
            return;
          }
          // 当日営業開始時刻
          const serviceStartTs = Math.min(...serviceTs);
          // 当日営業終了時刻
          const serviceEndTs = Math.max(...serviceTs);
          const SECONDS_IN_HOUR = 3600;
          const SECONDS_IN_MINUTE = 60;
          // 現在時刻
          const nowTime = today.getHours() * SECONDS_IN_HOUR + today.getMinutes() * SECONDS_IN_MINUTE;
          if (nowTime < serviceStartTs || nowTime > serviceEndTs) {
            // 現在時刻が営業時間外
            observe.next(false);
          } else {
            // 現在時刻が営業時間内
            observe.next(true);
          }
        } else {
          // サービス一覧なし
          observe.next(false);
        }
      }, () => {
        // 例外発生
        observe.error();
      });
    });
  }
}
